import { useCallback, useEffect, useMemo, useRef } from 'react'
import AriesIpcRenderer from '@renderer/ipc/AriesIpc.renderer'
import { type PermissionAssistantPanel } from '@shared/types/Aries'

/** Module-level wrappers: the IPC methods need their singleton `this`. */
export const presentPermissionAssistant = (panel: PermissionAssistantPanel): Promise<void> =>
  AriesIpcRenderer.presentPermissionAssistant(panel)
export const dismissPermissionAssistant = (): Promise<void> => AriesIpcRenderer.dismissPermissionAssistant()

/**
 * Manages the native "permission assistant" overlay — the floating helper that
 * opens System Settings and shows a drag-to-enable affordance for the
 * Accessibility / Screen panes. Tracks whether we've presented it so it's
 * always dismissed on unmount, and exposes idempotent present/dismiss.
 *
 * Shared by onboarding and Settings so both drive the same overlay.
 */
export function usePermissionAssistant(dismissAssistant: () => Promise<unknown>) {
  const hasPresented = useRef(false)
  const dismissAssistantRef = useRef(dismissAssistant)

  useEffect(() => {
    dismissAssistantRef.current = dismissAssistant
  }, [dismissAssistant])

  useEffect(() => {
    return () => {
      if (hasPresented.current) {
        dismissAssistantRef.current().catch(() => undefined)
      }
    }
  }, [])

  const present = useCallback(
    async (
      panel: PermissionAssistantPanel,
      presentAssistant: (panel: PermissionAssistantPanel) => Promise<unknown>,
    ) => {
      try {
        hasPresented.current = true
        await presentAssistant(panel)
      } catch (error) {
        console.error('Failed to present permission assistant:', error)
      }
    },
    [],
  )

  const dismiss = useCallback(async () => {
    if (!hasPresented.current) {
      return
    }
    hasPresented.current = false
    try {
      await dismissAssistantRef.current()
    } catch (error) {
      console.error('Failed to dismiss permission assistant:', error)
    }
  }, [])

  return useMemo(() => ({ present, dismiss }), [present, dismiss])
}
